"use client";

import { ListingStatusBadge } from "@/components/listings/listing-status-badge";
import { ListingStatusPickerPopover } from "@/components/listings/listing-status-picker-popover";
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import { useCurrentUser } from "@/hooks/use-current-user";
import { useErrorFormatter } from "@/hooks/use-error-formatter";
import { api } from "@/lib/api-client";
import type { DetailedListing } from "@/lib/types";
import {
  ADMIN_SETTABLE_LISTING_STATUSES,
  type AdminSettableListingStatus,
} from "@repo/utils";
import { useEffect, useState } from "react";

export type ListingPageStatusProps = {
  listing: DetailedListing;
};

export function ListingPageStatus({ listing }: ListingPageStatusProps) {
  const { user } = useCurrentUser();

  const { displayErrorToast } = useErrorFormatter();

  const isOwner =
    user && (user.id === listing.ownerId || user.id === listing.sellerAgentId);

  const [status, setStatus] = useState(listing.status);

  const changeStatusMutation = api.listings.changeStatus.useMutation();

  async function changeStatus(newStatus: AdminSettableListingStatus) {
    if (newStatus === status) {
      return;
    }

    setStatus(newStatus);

    try {
      await changeStatusMutation.mutateAsync({
        id: listing.id,
        status: newStatus,
      });
    } catch (err) {
      setStatus(listing.status);
      displayErrorToast(err);
    }
  }

  useEffect(() => {
    setStatus(listing.status);
  }, [listing.status]);

  if (
    !isOwner ||
    !ADMIN_SETTABLE_LISTING_STATUSES.includes(
      status as AdminSettableListingStatus,
    )
  ) {
    return <ListingStatusBadge listing={listing} />;
  }

  return (
    <ListingStatusPickerPopover
      type="single"
      types={ADMIN_SETTABLE_LISTING_STATUSES}
      selected={status as AdminSettableListingStatus}
      onSelect={changeStatus}
    >
      <Button
        variant="outline"
        className="h-8 rounded-md border-border bg-card hover:bg-muted"
        disabled={changeStatusMutation.isPending}
      >
        <ListingStatusBadge status={status} className="p-0 bg-transparent" />
        <Icon icon="ChevronDown" />
      </Button>
    </ListingStatusPickerPopover>
  );
}
